import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Boxes,
  Filter,
  Loader2,
  Plus,
  Search,
  X,
} from "lucide-react";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useMovements,
  useWarehouses,
} from "@/lib/api/inventory.queries";
import { useActiveCompany } from "@/lib/company-context";
import type { MovementKind } from "@/lib/api/inventory";
import { MovementKindBadge } from "@/components/inventory/MovementKindBadge";
import { MOVEMENT_KIND_LABEL, fmtDateTime, fmtQty } from "@/components/inventory/format";

export const Route = createFileRoute(
  "/companies/$companyId/inventario/movimientos/",
)({
  component: KardexPage,
});

function KardexPage() {
  return (
    <RequireAuth>
      <AppShell>
        <KardexContent />
      </AppShell>
    </RequireAuth>
  );
}

function KardexContent() {
  const fmtCop = useMoneyFormatter();
  const fmtCop2 = useMoneyFormatter();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const [warehouseId, setWarehouseId] = useState<string>("");
  const [kindFilter, setKindFilter] = useState<MovementKind | "">("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [search, setSearch] = useState<string>("");

  const warehousesQuery = useWarehouses(companyId);
  const movementsQuery = useMovements(companyId, {
    warehouseId: warehouseId || undefined,
    kind: kindFilter || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo ? `${dateTo}T23:59:59Z` : undefined,
    pageSize: 200,
  });

  const filtered = useMemo(() => {
    const all = movementsQuery.data?.movements ?? [];
    if (!search.trim()) return all;
    const needle = search.trim().toLowerCase();
    return all.filter(
      (m) =>
        m.productName?.toLowerCase().includes(needle) ||
        m.productSku?.toLowerCase().includes(needle) ||
        m.notes?.toLowerCase().includes(needle) ||
        m.sourceDocumentId?.toLowerCase().includes(needle),
    );
  }, [movementsQuery.data, search]);

  const summary = useMemo(() => {
    return filtered.reduce(
      (acc, m) => {
        if (m.direction === "in") {
          acc.inQty += m.qty;
          acc.inValue += m.totalCost;
        } else {
          acc.outQty += m.qty;
          acc.outValue += m.totalCost;
        }
        return acc;
      },
      { inQty: 0, outQty: 0, inValue: 0, outValue: 0 },
    );
  }, [filtered]);

  const hasFilters =
    !!warehouseId || !!kindFilter || !!dateFrom || !!dateTo || !!search;
  function clearFilters() {
    setWarehouseId("");
    setKindFilter("");
    setDateFrom("");
    setDateTo("");
    setSearch("");
  }

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1.5">
          <Link
            to="/companies/$companyId/inventario"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            ← Volver a Inventario
          </Link>
          <div className="flex items-center gap-2">
            <Boxes className="h-5 w-5 text-primary" />
            <h1 className="text-2xl font-semibold tracking-tight">
              Kardex de movimientos
            </h1>
          </div>
          <p className="max-w-2xl text-sm text-muted-foreground">
            Registro cronológico de cada entrada y salida. Cada fila muestra el
            saldo y el costo promedio inmediatamente después del movimiento, así
            puedes auditar la valuación en cualquier punto del tiempo.
          </p>
        </div>
        <Button asChild>
          <Link
            to="/companies/$companyId/inventario/movimientos/new"
            params={{ companyId }}
          >
            <Plus className="h-4 w-4" />
            Nuevo movimiento
          </Link>
        </Button>
      </header>

      {/* ── Filters bar ─────────────────────────────────────────────── */}
      <Card>
        <CardContent className="grid gap-3 py-4 lg:grid-cols-[1.5fr_1fr_1fr_0.7fr_0.7fr_auto]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder="Buscar producto, SKU o nota..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="rounded border bg-background px-2 py-2 text-sm"
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
          >
            <option value="">Todas las bodegas</option>
            {(warehousesQuery.data?.warehouses ?? []).map((w) => (
              <option key={w.id} value={w.id}>
                {w.code} — {w.name}
              </option>
            ))}
          </select>
          <select
            className="rounded border bg-background px-2 py-2 text-sm"
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as MovementKind | "")}
          >
            <option value="">Todos los tipos</option>
            {(Object.entries(MOVEMENT_KIND_LABEL) as [MovementKind, string][])
              .map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
          </select>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            placeholder="Desde"
          />
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            placeholder="Hasta"
          />
          {hasFilters ? (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="h-4 w-4" />
              Limpiar
            </Button>
          ) : (
            <div className="hidden items-center justify-center text-xs text-muted-foreground lg:flex">
              <Filter className="h-4 w-4" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Summary chips ──────────────────────────────────────────── */}
      {filtered.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-muted-foreground">
            {filtered.length} movimiento
            {filtered.length === 1 ? "" : "s"}
          </span>
          <Badge variant="success" className="gap-1">
            ↓ {fmtQty(summary.inQty)} unidades · {fmtCop(summary.inValue)}
          </Badge>
          <Badge variant="warning" className="gap-1">
            ↑ {fmtQty(summary.outQty)} unidades · {fmtCop(summary.outValue)}
          </Badge>
        </div>
      )}

      {/* ── Table ───────────────────────────────────────────────────── */}
      {movementsQuery.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : movementsQuery.isError ? (
        <ErrorState
          title="No pudimos cargar los movimientos"
          description={movementsQuery.error?.message ?? ""}
          onRetry={() => void movementsQuery.refetch()}
          retryLabel="Reintentar"
        />
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <Boxes className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {hasFilters
                ? "Sin movimientos para los filtros seleccionados."
                : "Aún no hay movimientos registrados."}
            </p>
            {hasFilters && (
              <Button variant="link" size="sm" onClick={clearFilters}>
                Limpiar filtros
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Producto</TableHead>
                <TableHead>Bodega</TableHead>
                <TableHead className="text-right">Cantidad</TableHead>
                <TableHead className="text-right">Costo unit.</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead className="text-right">Saldo qty</TableHead>
                <TableHead className="text-right">Saldo costo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    {fmtDateTime(m.createdAt)}
                  </TableCell>
                  <TableCell>
                    <MovementKindBadge kind={m.kind} short />
                  </TableCell>
                  <TableCell>
                    {m.productId ? (
                      <Link
                        to="/companies/$companyId/productos/$productId"
                        params={{ companyId, productId: m.productId }}
                        className="font-medium hover:underline"
                      >
                        {m.productName ?? m.productId.slice(0, 8)}
                      </Link>
                    ) : (
                      <span className="font-medium">—</span>
                    )}
                    {m.productSku && (
                      <span className="ml-2 font-mono text-xs text-muted-foreground">
                        {m.productSku}
                      </span>
                    )}
                    {m.notes && (
                      <p className="mt-0.5 max-w-xs truncate text-xs text-muted-foreground">
                        {m.notes}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="muted" className="font-mono">
                      {m.warehouseCode ?? "?"}
                    </Badge>
                  </TableCell>
                  <TableCell
                    className={`text-right tabular-nums ${
                      m.direction === "in"
                        ? "text-emerald-700 dark:text-emerald-400"
                        : "text-rose-700 dark:text-rose-400"
                    }`}
                  >
                    {m.direction === "in" ? "+" : "−"}
                    {fmtQty(m.qty)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {fmtCop2(m.unitCost)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmtCop(m.totalCost)}
                  </TableCell>
                  <TableCell
                    className={`text-right tabular-nums ${
                      m.qtyAfter < 0
                        ? "text-destructive"
                        : "text-muted-foreground"
                    }`}
                  >
                    {fmtQty(m.qtyAfter)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {fmtCop2(m.avgCostAfter)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {(movementsQuery.data?.movements.length ?? 0) >= 200 && (
            <div className="border-t px-4 py-2 text-center text-xs text-muted-foreground">
              Mostrando los 200 movimientos más recientes. Aplica un filtro de
              fechas para acotar.
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
